/**
 * Alshallal Pastry Company HR System - Access & Roles Page
 * Phase 18.10: Super Admin Only User Management
 * Phase 5: Permission-based UI gating
 * 
 * Access: Based on USER_VIEW_ALL, USER_CREATE, USER_EDIT, USER_DELETE permissions
 * 
 * Features:
 * - List users with search/filter
 * - Create new users
 * - Edit user roles/status
 * - Enable/Disable users (soft disable - no hard delete in UI)
 * 
 * Uses axios for API calls to avoid fetch body stream issues.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePermission, Permission } from '@/contexts/PermissionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Search, 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  UserCheck, 
  UserX,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Shield,
  Power,
  PowerOff,
  Info,
  Archive,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { PermissionGate } from '@/components/PermissionGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role display mappings
// The role dropdown, in ladder order. 2026-06 matrix: HR and IT are separated
// - HR Manager holds no system permission, IT INF holds no employee data.
const ROLE_DISPLAY = {
  employee: { en: 'Employee', ar: 'موظف', color: 'bg-slate-100 text-slate-800' },
  manager: { en: 'Manager', ar: 'مدير', color: 'bg-blue-100 text-blue-800' },
  hr_assistant: { en: 'HR Assistant', ar: 'مساعد الموارد البشرية', color: 'bg-teal-100 text-teal-800' },
  hr_manager: { en: 'HR Manager', ar: 'مدير الموارد البشرية', color: 'bg-purple-100 text-purple-800' },
  hr_admin: { en: 'HR Admin', ar: 'مسؤول الموارد البشرية', color: 'bg-amber-100 text-amber-800' },
  it_inf: { en: 'IT INF', ar: 'تقنية المعلومات والبنية التحتية', color: 'bg-cyan-100 text-cyan-800' },
  super_admin: { en: 'Super Admin', ar: 'المسؤول الأعلى', color: 'bg-red-100 text-red-800' },
};

// Still valid and still enforced by the backend, no longer offered for new
// assignments. Kept here so an account that already holds one shows its proper
// name instead of a raw string, and so it can still be found by the filter.
const LEGACY_ROLE_DISPLAY = {
  hr_ops: { en: 'HR Operations', ar: 'عمليات الموارد البشرية', color: 'bg-slate-100 text-slate-700' },
  hr_er: { en: 'Employee Relations', ar: 'علاقات الموظفين', color: 'bg-slate-100 text-slate-700' },
  hr_gr: { en: 'Government Relations', ar: 'العلاقات الحكومية', color: 'bg-slate-100 text-slate-700' },
  hr_ld: { en: 'Learning & Development', ar: 'التعلم والتطوير', color: 'bg-slate-100 text-slate-700' },
  hr_pc: { en: 'Payroll & Compensation', ar: 'الرواتب والمكافآت', color: 'bg-slate-100 text-slate-700' },
  hr_ta: { en: 'Talent Acquisition', ar: 'استقطاب المواهب', color: 'bg-slate-100 text-slate-700' },
  hr_sys: { en: 'HR System Admin', ar: 'مسؤول نظام الموارد البشرية', color: 'bg-slate-100 text-slate-700' },
  hr_aud: { en: 'HR Auditor', ar: 'مدقق الموارد البشرية', color: 'bg-slate-100 text-slate-700' },
};

const ALL_ROLE_DISPLAY = { ...ROLE_DISPLAY, ...LEGACY_ROLE_DISPLAY };

/**
 * Extract user-friendly error message from axios error response
 */
function extractErrorMessage(error, isRTL = false) {
  if (error.response?.data) {
    const data = error.response.data;
    
    // Handle Pydantic validation errors (array of errors)
    if (Array.isArray(data.detail)) {
      const messages = data.detail.map(e => {
        let msg = e.msg || e.message || '';
        // Clean up "Value error, " prefix
        if (msg.startsWith('Value error, ')) {
          msg = msg.replace('Value error, ', '');
        }
        return msg;
      }).filter(m => m);
      
      if (messages.length > 0) {
        return messages.join('; ');
      }
    }
    
    // Handle object detail with error field
    if (data.detail?.error) {
      return data.detail.error;
    }
    
    // Handle string detail
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Handle message field
    if (data.message) {
      return data.message;
    }
    
    // Handle error field
    if (data.error) {
      return data.error;
    }
  }
  
  // Fallback to error message
  return error.message || 'An unexpected error occurred';
}

/**
 * Make error message user-friendly
 */
function friendlyError(errorMsg, isRTL = false) {
  const lowerMsg = errorMsg.toLowerCase();
  
  if (lowerMsg.includes('already exists') || lowerMsg.includes('duplicate') || lowerMsg.includes('identifier already exists')) {
    return isRTL ? 'هذا البريد الإلكتروني مستخدم بالفعل' : 'A user with this email already exists';
  }
  
  if (lowerMsg.includes('already linked')) {
    return isRTL ? 'هذا الموظف مرتبط بحساب مستخدم آخر' : 'This employee is already linked to another user';
  }
  
  if (lowerMsg.includes('password') && lowerMsg.includes('8')) {
    return isRTL ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters';
  }
  
  if (lowerMsg.includes('password') && lowerMsg.includes('uppercase')) {
    return isRTL ? 'كلمة المرور يجب أن تحتوي على حرف كبير' : 'Password must contain at least one uppercase letter';
  }
  
  if (lowerMsg.includes('password') && lowerMsg.includes('lowercase')) {
    return isRTL ? 'كلمة المرور يجب أن تحتوي على حرف صغير' : 'Password must contain at least one lowercase letter';
  }
  
  if (lowerMsg.includes('password') && lowerMsg.includes('digit')) {
    return isRTL ? 'كلمة المرور يجب أن تحتوي على رقم' : 'Password must contain at least one digit';
  }
  
  // Handle "Field required" errors - be more specific
  if (lowerMsg.includes('field required')) {
    return isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields';
  }
  
  return errorMsg;
}

const AccessRolesPage = () => {
  const { token, hasRole, user: currentUser } = useAuth();
  const { isRTL } = useLanguage();
  const { hasPermission, hasAnyPermission } = usePermission();
  
  // Phase 5: Permission-based access
  const canViewUsers = hasPermission(Permission.USER_VIEW_ALL);
  const canCreateUser = hasPermission(Permission.USER_CREATE);
  const canEditUser = hasPermission(Permission.USER_EDIT);
  const canDeleteUser = hasPermission(Permission.USER_DELETE);
  const canAssignRoles = hasPermission(Permission.USER_ROLE_ASSIGN);
  const canArchiveUser = hasPermission(Permission.USER_ARCHIVE);
  const canDeactivateUser = hasPermission(Permission.USER_DEACTIVATE);
  
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [toggleStatusDialogOpen, setToggleStatusDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Phase 21: Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmToken, setDeleteConfirmToken] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    id_number: '',  // Unified field for National ID (1xxx) or Iqama (2xxx)
    role: 'employee',
    is_active: true,
  });
  const [idNumberError, setIdNumberError] = useState('');
  const [reason, setReason] = useState('');
  const [linkEmployee, setLinkEmployee] = useState(true);
  const [matchedEmployee, setMatchedEmployee] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [employeeHasUser, setEmployeeHasUser] = useState(false);
  
  // RBAC check - Super Admin ONLY
  // 2026-06: account administration is HR Manager, HR Admin, IT INF and the
  // owner. It used to be Super Admin only, which is why HR had to ask for every
  // new hire's login.
  const canAccess = hasRole('super_admin', 'hr_manager', 'hr_admin', 'it_inf');
  
  // Axios instance with auth header - memoized to prevent re-renders
  const api = useMemo(() => axios.create({
    baseURL: API_URL,
    headers: { 'Authorization': `Bearer ${token}` }
  }), [token]);
  
  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setFetchError(null);
    try {
      const params = { page, page_size: 10 };
      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      
      const response = await api.get('/api/users/', { params });
      setUsers(response.data.users || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
      setSelectedIds([]);  // Clear selection on data refresh
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMsg = extractErrorMessage(error, isRTL);
      setFetchError(errorMsg);
      toast.error(isRTL ? 'فشل في تحميل المستخدمين' : 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter, canAccess, isRTL, api]);
  
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  
  // Check for employee match when email or id_number changes
  const checkEmployeeMatch = useCallback(async (email, idNumber) => {
    if (!email && !idNumber) {
      setMatchedEmployee(null);
      setEmployeeHasUser(false);
      return;
    }
    
    setMatchLoading(true);
    try {
      const params = {};
      if (email) params.email = email;
      
      // Route ID to correct field based on first digit
      if (idNumber && idNumber.length >= 1) {
        const cleanedId = idNumber.replace(/\D/g, '');
        if (cleanedId.startsWith('1')) {
          params.national_id = cleanedId;
        } else if (cleanedId.startsWith('2')) {
          params.iqama = cleanedId;
        }
      }
      
      const response = await api.get('/api/users/match-employee', { params });
      
      if (response.data?.matched) {
        setMatchedEmployee(response.data.employee);
        setEmployeeHasUser(!!response.data.employee?.has_user);
      } else {
        setMatchedEmployee(null);
        setEmployeeHasUser(false);
      }
    } catch (error) {
      setMatchedEmployee(null);
      setEmployeeHasUser(false);
    }
    setMatchLoading(false);
  }, [api]);
  
  // Debounced employee match check
  useEffect(() => {
    if (!createDialogOpen || !linkEmployee) return;
    
    const timer = setTimeout(() => {
      checkEmployeeMatch(formData.email, formData.id_number);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.email, formData.id_number, linkEmployee, createDialogOpen, checkEmployeeMatch]);
  
  // Check if role requires employee (2026-06: HR Assistant is on the payroll
  // like the rest of HR; IT INF and Super Admin are system accounts)
  const roleRequiresEmployee = ['employee', 'manager', 'hr_assistant', 'hr_manager', 'hr_admin'].includes(formData.role);
  
  // Determine if Create button should be disabled
  const isCreateDisabled = () => {
    if (submitting) return true;
    if (!formData.email || !formData.password) return true;
    if (matchLoading) return true;
    if (linkEmployee && matchedEmployee && employeeHasUser) return true;
    if (idNumberError) return true;  // Disable if ID validation fails
    return false;
  };
  
  // Create user
  const handleCreate = async () => {
    if (linkEmployee && employeeHasUser) {
      toast.error(isRTL 
        ? 'هذا الموظف لديه حساب دخول بالفعل. استخدم التعديل بدلاً من ذلك.'
        : 'This employee already has a login. Use Edit instead.');
      return;
    }
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('link_employee', linkEmployee.toString());
      params.append('auto_create_employee', 'true');
      
      // Clean form data - remove empty strings that would trigger validation
      const cleanedData = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
        is_active: formData.is_active,
      };
      // Only include optional fields if they have values
      if (formData.first_name?.trim()) cleanedData.first_name = formData.first_name.trim();
      if (formData.last_name?.trim()) cleanedData.last_name = formData.last_name.trim();
      
      // Route ID number to correct field based on first digit
      if (formData.id_number?.trim()) {
        const cleanedId = formData.id_number.trim().replace(/\D/g, '');
        if (cleanedId.startsWith('1')) {
          cleanedData.national_id = cleanedId;
        } else if (cleanedId.startsWith('2')) {
          cleanedData.iqama = cleanedId;
        }
      }
      
      await api.post(`/api/users/?${params}`, cleanedData);
      
      // Success
      let successMessage;
      if (roleRequiresEmployee) {
        if (matchedEmployee) {
          const empName = `${matchedEmployee.personal?.first_name || matchedEmployee.first_name || ''} ${matchedEmployee.personal?.last_name || matchedEmployee.last_name || ''}`.trim();
          successMessage = isRTL 
            ? `تم إنشاء المستخدم وربطه بالموظف: ${empName}`
            : `User created and linked to employee: ${empName}`;
        } else {
          successMessage = isRTL 
            ? 'تم إنشاء المستخدم وتم إنشاء ملف موظف تلقائياً'
            : 'User created and employee profile auto-created';
        }
      } else {
        successMessage = isRTL ? 'تم إنشاء المستخدم بنجاح' : 'User created successfully';
      }
      
      toast.success(successMessage);
      setCreateDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, isRTL);
      const userFriendlyMsg = friendlyError(errorMsg, isRTL);
      toast.error(userFriendlyMsg);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Update user (role/status)
  const handleUpdate = async () => {
    if (!selectedUser) return;
    
    if (formData.role !== selectedUser.role && !reason.trim()) {
      toast.error(isRTL ? 'السبب مطلوب لتغيير الدور' : 'Reason is required for role changes');
      return;
    }
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      if (reason.trim()) params.append('reason', reason);
      
      await api.patch(`/api/users/${selectedUser.id}?${params}`, {
        role: formData.role,
        is_active: formData.is_active,
      });
      
      toast.success(isRTL ? 'تم تحديث المستخدم بنجاح' : 'User updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, isRTL);
      toast.error(friendlyError(errorMsg, isRTL));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Toggle user status
  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    
    if (!reason.trim()) {
      toast.error(isRTL ? 'السبب مطلوب' : 'Reason is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const newStatus = !selectedUser.is_active;
      const params = new URLSearchParams();
      params.append('reason', reason);
      
      await api.patch(`/api/users/${selectedUser.id}?${params}`, { is_active: newStatus });
      
      toast.success(newStatus 
        ? (isRTL ? 'تم تفعيل المستخدم' : 'User enabled') 
        : (isRTL ? 'تم تعطيل المستخدم' : 'User disabled'));
      setToggleStatusDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, isRTL);
      toast.error(friendlyError(errorMsg, isRTL));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      id_number: '',  // Unified field for National ID (1xxx) or Iqama (2xxx)
      role: 'employee',
      is_active: true,
    });
    setReason('');
    setSelectedUser(null);
    setLinkEmployee(true);
    setMatchedEmployee(null);
    setEmployeeHasUser(false);
    setIdNumberError('');
  };
  
  // Open edit dialog
  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      role: user.role,
      is_active: user.is_active,
    });
    setReason('');
    setEditDialogOpen(true);
  };
  
  // Open toggle status dialog
  const openToggleStatusDialog = (user) => {
    setSelectedUser(user);
    setReason('');
    setToggleStatusDialogOpen(true);
  };
  
  // Phase 21: Handle bulk archive
  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    
    setSubmitting(true);
    try {
      const response = await api.post('/api/users/bulk-archive', {
        ids: selectedIds,
        reason: archiveReason || undefined
      });
      
      const data = response.data;
      
      if (data.archived?.length > 0) {
        toast.success(isRTL 
          ? `تم أرشفة ${data.archived.length} مستخدم(ين) بنجاح`
          : `Successfully archived ${data.archived.length} user(s)`
        );
      }
      
      if (data.skipped?.length > 0) {
        data.skipped.forEach(item => {
          toast.warning(`${item.id}: ${item.reason}`);
        });
      }
      
      setBulkArchiveDialogOpen(false);
      setArchiveReason('');
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, isRTL);
      toast.error(friendlyError(errorMsg, isRTL));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Phase 21: Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    const expectedToken = `DELETE ${selectedIds.length}`;
    if (deleteConfirmToken.toUpperCase() !== expectedToken.toUpperCase()) {
      toast.error(isRTL 
        ? `يرجى كتابة "${expectedToken}" للتأكيد`
        : `Please type "${expectedToken}" to confirm`
      );
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/api/users/bulk-delete', {
        ids: selectedIds,
        confirm_token: deleteConfirmToken,
        reason: deleteReason || undefined
      });
      
      const data = response.data;
      
      if (data.success && data.deleted?.length > 0) {
        toast.success(isRTL 
          ? `تم حذف ${data.deleted.length} مستخدم(ين) نهائياً`
          : `Permanently deleted ${data.deleted.length} user(s)`
        );
      } else if (!data.success && data.failed?.length > 0) {
        toast.error(data.failed[0]?.error || 'Delete failed');
      }
      
      if (data.skipped?.length > 0) {
        data.skipped.forEach(item => {
          toast.warning(`${item.id}: ${item.reason}`);
        });
      }
      
      setBulkDeleteDialogOpen(false);
      setDeleteReason('');
      setDeleteConfirmToken('');
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, isRTL);
      toast.error(friendlyError(errorMsg, isRTL));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Access denied view
  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {isRTL ? 'غير مصرح' : 'Access Denied'}
        </h2>
        <p className="text-slate-600">
          {isRTL 
            ? 'فقط المسؤول الأعلى يمكنه الوصول إلى إدارة المستخدمين'
            : 'Only Super Admin can access user management'}
        </p>
      </div>
    );
  }
  
  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header - responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              {isRTL ? 'إدارة الوصول والأدوار' : 'Access & Roles'}
            </h1>
          </div>
          <p className="text-slate-600 text-xs sm:text-sm mt-1">
            {isRTL ? 'إدارة حسابات المستخدمين وأدوارهم' : 'Manage user accounts and roles'}
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setCreateDialogOpen(true); }}
          className="w-full sm:w-auto"
          data-testid="add-user-button"
        >
          <Plus className="h-4 w-4 me-2" />
          {isRTL ? 'مستخدم جديد' : 'New User'}
        </Button>
      </div>
      
      {/* Filters - responsive */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <div className="relative flex-1">
              <Search className={cn(
                "absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400",
                isRTL ? "right-3" : "left-3"
              )} />
              <Input
                placeholder={isRTL ? 'بحث بالاسم أو البريد...' : 'Search by name or email...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn(isRTL ? "pr-10" : "pl-10")}
              />
            </div>
            <div className="flex gap-2 sm:gap-4">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="flex-1 sm:flex-none sm:w-[180px]" data-testid="role-filter-select">
                  <SelectValue placeholder={isRTL ? 'جميع الأدوار' : 'All Roles'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الأدوار' : 'All Roles'}</SelectItem>
                  {Object.entries(ROLE_DISPLAY).map(([key, value]) => (
                    <SelectItem key={key} value={key} data-testid={`role-filter-option-${key}`}>
                      {isRTL ? value.ar : value.en}
                    </SelectItem>
                  ))}
                  {Object.entries(LEGACY_ROLE_DISPLAY).map(([key, value]) => (
                    <SelectItem key={key} value={key} data-testid={`role-filter-option-${key}`}>
                      {(isRTL ? value.ar : value.en) + (isRTL ? ' (سابق)' : ' (legacy)')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 sm:flex-none sm:w-[150px]">
                  <SelectValue placeholder={isRTL ? 'الحالة' : 'Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</SelectItem>
                  <SelectItem value="active">{isRTL ? 'نشط' : 'Active'}</SelectItem>
                  <SelectItem value="inactive">{isRTL ? 'غير نشط' : 'Inactive'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Users Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <CardTitle className="text-base font-medium">
              {isRTL ? `${total} مستخدم` : `${total} Users`}
            </CardTitle>
            
            {/* Phase 21: Bulk Actions Toolbar */}
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {isRTL ? `${selectedIds.length} محدد` : `${selectedIds.length} selected`}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkArchiveDialogOpen(true)}
                >
                  <Archive className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                  {isRTL ? 'أرشفة' : 'Archive'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                >
                  <Trash2 className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                  {isRTL ? 'حذف نهائي' : 'Delete'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : fetchError ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-3" />
              <p className="text-slate-600 mb-4">
                {isRTL ? 'فشل في تحميل المستخدمين' : 'Failed to load users'}
              </p>
              <p className="text-sm text-slate-500 mb-4">{fetchError}</p>
              <Button variant="outline" onClick={fetchUsers}>
                {isRTL ? 'إعادة المحاولة' : 'Retry'}
              </Button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {isRTL ? 'لا يوجد مستخدمين' : 'No users found'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="min-w-[500px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.length === users.length && users.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedIds(users.map(u => u.id));
                            } else {
                              setSelectedIds([]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>{isRTL ? 'المستخدم' : 'User'}</TableHead>
                      <TableHead className="hidden sm:table-cell">{isRTL ? 'البريد الإلكتروني' : 'Email'}</TableHead>
                      <TableHead>{isRTL ? 'الدور' : 'Role'}</TableHead>
                      <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} className={!user.is_active ? 'opacity-60' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedIds(prev => [...prev, user.id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== user.id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">
                            {user.first_name || user.last_name 
                              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                              : '-'}
                          </div>
                          <div className="text-xs text-slate-500 sm:hidden">{user.email}</div>
                          {user.national_id && (
                            <div className="text-xs text-slate-500">{user.national_id}</div>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={cn(
                          "font-normal",
                          ALL_ROLE_DISPLAY[user.role]?.color || 'bg-slate-100'
                        )} data-testid={`user-role-badge-${user.id}`}>
                          {isRTL 
                            ? ALL_ROLE_DISPLAY[user.role]?.ar || user.role
                            : ALL_ROLE_DISPLAY[user.role]?.en || user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <UserCheck className="h-3 w-3 me-1" />
                            {isRTL ? 'نشط' : 'Active'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            <UserX className="h-3 w-3 me-1" />
                            {isRTL ? 'معطل' : 'Disabled'}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <Pencil className="h-4 w-4 me-2" />
                              {isRTL ? 'تعديل الدور' : 'Edit Role'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.id !== currentUser?.id && (
                              <DropdownMenuItem 
                                onClick={() => openToggleStatusDialog(user)}
                                className={user.is_active ? "text-amber-600" : "text-green-600"}
                              >
                                {user.is_active ? (
                                  <>
                                    <PowerOff className="h-4 w-4 me-2" />
                                    {isRTL ? 'تعطيل' : 'Disable'}
                                  </>
                                ) : (
                                  <>
                                    <Power className="h-4 w-4 me-2" />
                                    {isRTL ? 'تفعيل' : 'Enable'}
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-500">
                    {isRTL 
                      ? `صفحة ${page} من ${totalPages}`
                      : `Page ${page} of ${totalPages}`}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'إنشاء مستخدم جديد' : 'Create New User'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'أدخل بيانات المستخدم الجديد' : 'Enter the details for the new user'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'الاسم الأول' : 'First Name'}</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'اسم العائلة' : 'Last Name'}</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'البريد الإلكتروني' : 'Email'} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'كلمة المرور' : 'Password'} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isRTL ? 'مثال: Test@123' : 'e.g., Test@123'}
              />
              <p className="text-xs text-slate-500">
                {isRTL 
                  ? 'الحد الأدنى 8 أحرف، حرف كبير، حرف صغير، رقم. سيُطلب من المستخدم تغييرها عند أول تسجيل دخول.'
                  : 'Min 8 chars, 1 uppercase, 1 lowercase, 1 number. User must change on first login.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'رقم الهوية / الإقامة' : 'ID / Iqama Number'}</Label>
              <Input
                value={formData.id_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, id_number: value });
                  // Client-side validation
                  if (value && value.length > 0) {
                    if (value.length !== 10) {
                      setIdNumberError(isRTL ? 'يجب أن يكون 10 أرقام' : 'Must be 10 digits');
                    } else if (!value.startsWith('1') && !value.startsWith('2')) {
                      setIdNumberError(isRTL ? 'يجب أن يبدأ بـ 1 أو 2' : 'Must start with 1 or 2');
                    } else {
                      setIdNumberError('');
                    }
                  } else {
                    setIdNumberError('');
                  }
                }}
                placeholder={isRTL ? '10 أرقام' : '10 digits'}
                maxLength={10}
              />
              <p className="text-xs text-slate-500">
                {isRTL 
                  ? '10 أرقام. يبدأ بـ 1 (هوية وطنية) أو 2 (إقامة)'
                  : '10 digits. Starts with 1 (National ID) or 2 (Iqama).'}
              </p>
              {idNumberError && (
                <p className="text-xs text-red-500">{idNumberError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الدور' : 'Role'}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="create-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_DISPLAY).map(([key, value]) => (
                    <SelectItem key={key} value={key} data-testid={`create-user-role-option-${key}`}>
                      {isRTL ? value.ar : value.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Employee Linking Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <Label className="text-sm font-medium">
                  {isRTL ? 'ربط بملف الموظف تلقائياً' : 'Link to employee automatically'}
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isRTL 
                    ? 'البحث عن موظف مطابق بالبريد الإلكتروني أو رقم الهوية'
                    : 'Search for matching employee by email or national ID'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={linkEmployee}
                onClick={() => setLinkEmployee(!linkEmployee)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                  linkEmployee ? "bg-blue-600" : "bg-slate-200"
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition",
                    linkEmployee ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>
            
            {/* Employee Match Preview */}
            {linkEmployee && roleRequiresEmployee && (
              <div className={cn(
                "p-3 rounded-lg border",
                matchLoading ? "bg-slate-50 border-slate-200" :
                matchedEmployee ? (employeeHasUser ? "bg-amber-50 border-amber-300" : "bg-green-50 border-green-200") : "bg-amber-50 border-amber-200"
              )}>
                {matchLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isRTL ? 'جاري البحث...' : 'Searching...'}
                  </div>
                ) : matchedEmployee ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {employeeHasUser ? (
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      ) : (
                        <UserCheck className="h-4 w-4 text-green-600" />
                      )}
                      <span className={cn("text-sm font-medium", employeeHasUser ? "text-amber-800" : "text-green-800")}>
                        {isRTL ? 'تم العثور على موظف مطابق' : 'Matching employee found'}
                      </span>
                    </div>
                    <div className={cn("text-sm", employeeHasUser ? "text-amber-700" : "text-green-700")}>
                      <span className="font-medium">
                        {matchedEmployee.personal?.first_name || matchedEmployee.first_name || ''} {matchedEmployee.personal?.last_name || matchedEmployee.last_name || ''}
                      </span>
                      {(matchedEmployee.employment?.job_title || matchedEmployee.job_title) && (
                        <span className={employeeHasUser ? "text-amber-600" : "text-green-600"}> - {matchedEmployee.employment?.job_title || matchedEmployee.job_title}</span>
                      )}
                    </div>
                    {employeeHasUser && (
                      <div className="flex items-center gap-2 mt-2 p-2 bg-amber-100 rounded">
                        <Info className="h-4 w-4 text-amber-700" />
                        <p className="text-xs text-amber-800 font-medium">
                          {isRTL 
                            ? 'هذا الموظف لديه حساب دخول بالفعل. استخدم التعديل بدلاً من ذلك.'
                            : 'This employee already has a login. Use Edit instead.'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (formData.email || formData.id_number) ? (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {isRTL ? 'لم يتم العثور على موظف مطابق' : 'No matching employee found'}
                      </p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        {isRTL 
                          ? 'سيتم إنشاء ملف موظف تلقائياً عند إنشاء المستخدم'
                          : 'An employee profile will be auto-created when user is created'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    {isRTL 
                      ? 'أدخل البريد الإلكتروني أو رقم الهوية للبحث عن موظف'
                      : 'Enter email or national ID to search for employee'}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={isCreateDisabled()}
            >
              {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'إنشاء' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تعديل دور المستخدم' : 'Edit User Role'}</DialogTitle>
            <DialogDescription>{selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'الدور' : 'Role'}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_DISPLAY).map(([key, value]) => (
                    <SelectItem key={key} value={key} data-testid={`edit-user-role-option-${key}`}>
                      {isRTL ? value.ar : value.en}
                    </SelectItem>
                  ))}
                  {/* A legacy role is not offered for new assignments, but the
                      account holding one must still show it - otherwise the
                      field renders blank and any save silently reassigns. */}
                  {selectedUser?.role && LEGACY_ROLE_DISPLAY[selectedUser.role] && (
                    <SelectItem
                      value={selectedUser.role}
                      data-testid={`edit-user-role-option-${selectedUser.role}`}
                    >
                      {(isRTL
                        ? LEGACY_ROLE_DISPLAY[selectedUser.role].ar
                        : LEGACY_ROLE_DISPLAY[selectedUser.role].en)
                        + (isRTL ? ' (سابق)' : ' (legacy)')}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {formData.role !== selectedUser?.role && (
              <div className="space-y-2">
                <Label>{isRTL ? 'سبب تغيير الدور' : 'Reason for Role Change'} *</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={isRTL ? 'أدخل سبب التغيير...' : 'Enter reason for change...'}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleUpdate} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Toggle Status Dialog */}
      <Dialog open={toggleStatusDialogOpen} onOpenChange={setToggleStatusDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className={selectedUser?.is_active ? "text-amber-600" : "text-green-600"}>
              {selectedUser?.is_active 
                ? (isRTL ? 'تعطيل المستخدم' : 'Disable User')
                : (isRTL ? 'تفعيل المستخدم' : 'Enable User')}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.is_active 
                ? (isRTL 
                    ? `سيتم تعطيل حساب "${selectedUser?.email}". لن يتمكن من تسجيل الدخول.`
                    : `"${selectedUser?.email}" will be disabled and cannot login.`)
                : (isRTL 
                    ? `سيتم تفعيل حساب "${selectedUser?.email}". سيتمكن من تسجيل الدخول مرة أخرى.`
                    : `"${selectedUser?.email}" will be enabled and can login again.`)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'السبب' : 'Reason'} *</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={isRTL ? 'أدخل السبب...' : 'Enter reason...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleStatusDialogOpen(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              variant={selectedUser?.is_active ? "destructive" : "default"}
              onClick={handleToggleStatus} 
              disabled={submitting || !reason.trim()}
            >
              {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {selectedUser?.is_active 
                ? (isRTL ? 'تعطيل' : 'Disable')
                : (isRTL ? 'تفعيل' : 'Enable')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Phase 21: Bulk Archive Dialog */}
      <Dialog open={bulkArchiveDialogOpen} onOpenChange={(open) => { setBulkArchiveDialogOpen(open); if (!open) setArchiveReason(''); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              {isRTL ? 'أرشفة المستخدمين' : 'Archive Users'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? `سيتم أرشفة ${selectedIds.length} مستخدم(ين). لن يتمكنوا من تسجيل الدخول.`
                : `${selectedIds.length} user(s) will be archived. They will not be able to login.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                {isRTL 
                  ? 'المستخدمين المؤرشفين يمكن استعادتهم من صفحة المستودع.'
                  : 'Archived users can be restored from the Repository page.'
                }
              </p>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'السبب (اختياري)' : 'Reason (optional)'}</Label>
              <Textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الأرشفة...' : 'Enter reason for archiving...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkArchiveDialogOpen(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleBulkArchive} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'أرشفة' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Phase 21: Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(open) => { setBulkDeleteDialogOpen(open); if (!open) { setDeleteReason(''); setDeleteConfirmToken(''); } }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {isRTL ? 'حذف نهائي' : 'Permanent Delete'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'تحذير: هذا الإجراء لا يمكن التراجع عنه!'
                : 'Warning: This action cannot be undone!'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">
                {isRTL 
                  ? `سيتم حذف ${selectedIds.length} مستخدم(ين) نهائياً.`
                  : `${selectedIds.length} user(s) will be permanently deleted.`
                }
              </p>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {users.filter(u => selectedIds.includes(u.id)).slice(0, 5).map(u => (
                  <li key={u.id}>{u.email}</li>
                ))}
                {selectedIds.length > 5 && (
                  <li>{isRTL ? `و ${selectedIds.length - 5} آخرين...` : `and ${selectedIds.length - 5} more...`}</li>
                )}
              </ul>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'السبب (اختياري)' : 'Reason (optional)'}</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الحذف...' : 'Enter reason for deletion...'}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {isRTL 
                  ? `اكتب "DELETE ${selectedIds.length}" للتأكيد:`
                  : `Type "DELETE ${selectedIds.length}" to confirm:`
                }
              </Label>
              <Input
                value={deleteConfirmToken}
                onChange={(e) => setDeleteConfirmToken(e.target.value)}
                placeholder={`DELETE ${selectedIds.length}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleBulkDelete} 
              disabled={submitting || deleteConfirmToken.toUpperCase() !== `DELETE ${selectedIds.length}`}
            >
              {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'حذف نهائي' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessRolesPage;
